import { ROUTE_LABELS } from "@/B2C/features/lovable/utils";
import { App_Url_New } from "@/shared/common/static";
import CommonLogoutModal from "@/shared/components/CommonLogoutModal";
import { SecureNavBarItems } from "@/shared/config/header.config";
import { axiosInstance } from "@/shared/lib/axiosInstance";
import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import { setMemberList } from "@/shared/redux/reducers/masterSlice";
import { openModal } from "@/shared/redux/reducers/uiSlice";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { ArrowLeft, Bell, Search } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

export default function DashboardHeader(): React.JSX.Element {
  const pageShellClasses = getThemeComponentGroup("pageShell");
  const cardClasses = getThemeComponentGroup("card");
  const {
    auth: { user, session },
    masterSlice: { memberList },
  } = usePosterReducers();
  const { pathname, search } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const canGoBack = window.history.state?.idx > 0;
  const normalizedPathname =
    pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const secureRootPaths = useMemo(
    () =>
      SecureNavBarItems.map((item) => item.path)
        .filter((path): path is string => Boolean(path))
        .map((path) => (path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path)),
    []
  );
  const shouldHideBackButton = secureRootPaths.includes(normalizedPathname);
  const shouldShowBackButton = canGoBack && !shouldHideBackButton;

  const isEditQuoteRoute = pathname === App_Url_New.B2CPageUrl.editQuote;

  useEffect(() => {
    if (!isEditQuoteRoute) return;

    let isMounted = true;

    const fetchTeamMembers = async () => {
      try {
        const response = await axiosInstance.post(App_Url_New.ApiEndPoint.teamMembers, {
          page: 1,
          page_size: 5000,
          search: "",
          ordering: null,
          filters: {},
        });

        if (!isMounted) return;
        dispatch(setMemberList(response?.data));
      } catch (error) {
        console.error("Failed to fetch team members on edit quote page:", error);
      }
    };

    fetchTeamMembers();

    return () => {
      isMounted = false;
    };
  }, [dispatch, isEditQuoteRoute]);

  const handleHeaderBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(App_Url_New.B2CPageUrl.quotes);
  };

  const formatPageLabel = (value: string): string => {
    return decodeURIComponent(value)
      .replace(/[-_]+/g, " ")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getPageTitle = (path) => {
    const cleanPath = path.split("?")[0].split("#")[0];
    const segments = cleanPath.split("/").filter(Boolean);

    // For Lovable iframe routes, show the section name (side nav label) instead of the route.
    const firstSegment = segments[0] ?? "";
    if (ROUTE_LABELS[firstSegment]) {
      return ROUTE_LABELS[firstSegment];
    }

    const queryParams = new URLSearchParams(search);
    const nameParam = queryParams.get("name");

    if (nameParam) {
      return formatPageLabel(nameParam);
    }

    const lastSegment = segments[segments.length - 1] || "dashboard";
    return formatPageLabel(lastSegment);
  };

  const title = getPageTitle(pathname);
  const editQuoteId = useMemo(() => {
    if (!isEditQuoteRoute) {
      return "";
    }

    const queryParams = new URLSearchParams(search);
    const quoteId = queryParams.get("quoteId")?.trim();

    return quoteId ? `QT${quoteId}` : "";
  }, [isEditQuoteRoute, search]);
  const assignedAgentName = useMemo(() => {
    const sessionQuote = session?.session_quotes;
    const nameFromPayload =
      sessionQuote?.assigned_agent_name ||
      sessionQuote?.assign_agent_name ||
      sessionQuote?.user?.full_name ||
      sessionQuote?.assigned_user?.full_name ||
      sessionQuote?.assign_user?.full_name ||
      "";

    if (nameFromPayload) {
      return nameFromPayload;
    }

    const assignedId =
      sessionQuote?.assign_agent_id ??
      sessionQuote?.assigned_agent_id ??
      sessionQuote?.assigned_user_id ??
      sessionQuote?.assign_user_id ??
      sessionQuote?.assigned_user?.id ??
      sessionQuote?.assign_user?.id ??
      sessionQuote?.user?.id;

    if (!assignedId) {
      return "";
    }

    const candidates: any[] = [
      ...(Array.isArray(memberList) ? memberList : []),
      ...(Array.isArray(session?.users) ? session.users : []),
      ...(Array.isArray(session?.members) ? session.members : []),
    ];

    const matchedCandidate = candidates.find((candidate: any) => {
      const candidateId =
        candidate?.employee?.id ??
        candidate?.employee_id ??
        candidate?.employeeId ??
        candidate?.assigned_user_id ??
        candidate?.user_id ??
        candidate?.id;

      return String(candidateId) === String(assignedId);
    });

    return (
      matchedCandidate?.employee?.full_name ||
      matchedCandidate?.full_name ||
      matchedCandidate?.name ||
      ""
    );
  }, [memberList, session]);
  const effectiveTitle = isEditQuoteRoute
    ? assignedAgentName
      ? `Edit${editQuoteId ? ` | ${editQuoteId}` : ""} | Assigned To: ${assignedAgentName}`
      : `Edit${editQuoteId ? ` | ${editQuoteId}` : ""} | Agent Unassigned`
    : title;
  const getInitials = (fullName) => {
    if (!fullName) return "U";

    const names = fullName.trim().split(" ").filter(Boolean);

    if (names.length === 1) {
      return names[0][0].toUpperCase();
    }

    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  const userName = getInitials(user?.user?.full_name);
  console.log("title", userName);

  // function callLogout() {
  //   dispatch(logout());

  //   setTimeout(() => {navigate(App_Url_New.CommonPageUrl.login);
  //   }, 0);
  // }

  const handleSignOut = () => {
    dispatch(
      openModal({
        name: App_Url_New.PopupKeys.LOGOUT_CONFIRM,
        title: "Logout?",
        className: "max-w-md",
        children: <CommonLogoutModal displayName={userName} />,
      })
    );
  };

  return (
    <header
      className={`h-16 border-b flex items-center justify-between px-4 sticky top-0 z-80 ${getThemeClass(cardClasses.bgColor, "bg-theme-surface")} ${getThemeClass(pageShellClasses.borderColor, "border-theme-border/70")}`}
    >
      <div className="flex items-center gap-2">
        {/* {!isSidebarOpen && (
          <button onClick={onOpenSidebar} className="md:hidden p-2">
            <Menu size={20} />
          </button>
        )} */}
        {shouldShowBackButton && (
          <button
            type="button"
            onClick={handleHeaderBack}
            className={`inline-flex items-center gap-1 rounded-full p-2 text-sm ${getThemeClass(pageShellClasses.mutedTextColor, "text-theme-text-soft")} hover:bg-theme-accent/55`}
          >
            <ArrowLeft size={20} />
          </button>
        )}
        <h1
          className={`text-lg font-bold hidden sm:block ${getThemeClass(pageShellClasses.textColor, "text-theme-text")}`}
        >
          {effectiveTitle}
        </h1>
      </div>

      <div className="flex items-center gap-4 md:gap-8">
        <div className="relative hidden lg:block">
          <Search
            className={`absolute left-3 top-1/2 -translate-y-1/2 ${getThemeClass(pageShellClasses.mutedTextColor, "text-theme-text-soft")}`}
            size={16}
          />
          {/* <input
            type="text"
            placeholder={searchPlaceholder || "Search..."}
            value={searchQuery || ""}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="pl-10 pr-4 py-2 bg-theme-surface-muted border border-theme-border rounded-lg text-sm w-64 focus:ring-2 focus:ring-theme-accent outline-none"
          /> */}
        </div>
        <div className="flex items-center gap-3">
          <button
            className={`p-2 rounded-full relative ${getThemeClass(pageShellClasses.mutedTextColor, "text-theme-text-soft")} hover:bg-theme-surface-muted`}
          >
            <Bell size={20} />
            <span
              className={`absolute top-2 right-2.5 w-2 h-2 bg-theme-danger-bright rounded-full border-2 ${getThemeClass(cardClasses.bgColor, "border-theme-surface")}`}
            ></span>
          </button>
          <UserAvatarDropdown
            userName={userName}
            menuItems={[
              { label: "Profile", onClick: () => console.log("Profile") },
              { label: "Settings", onClick: () => console.log("Settings") },
              {
                label: "Logout",
                danger: true,
                onClick: handleSignOut,
              },
            ]}
          />
        </div>
      </div>
    </header>
  );
}

const UserAvatarDropdown = ({ userName = "U", menuItems = [] }) => {
  const avatarClasses = getThemeComponentGroup("avatar");
  const menuClasses = getThemeComponentGroup("menu");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Avatar */}
      <div
        onClick={() => setOpen(!open)}
        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold cursor-pointer select-none ${getThemeClass(avatarClasses.badgeBgColor, "bg-theme-primary-alt")} ${getThemeClass(avatarClasses.badgeTextColor, "text-theme-text-inverse")}`}
      >
        {userName}
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className={`absolute right-0 mt-2 w-44 shadow-lg rounded-md border z-90 ${getThemeClass(menuClasses.panelBgColor, "bg-theme-surface")} ${getThemeClass(menuClasses.panelBorderColor, "border-theme-border")}`}
        >
          <ul
            className={`text-sm ${getThemeClass(menuClasses.itemTextColor, "text-theme-text-muted")}`}
          >
            {menuItems.map((item, index) => (
              <li
                key={index}
                onClick={() => {
                  item.onClick?.();
                  setOpen(false);
                }}
                className={`px-4 py-3 cursor-pointer ${getThemeClass(menuClasses.itemHoverBgColor, "hover:bg-theme-surface-soft")} ${
                  item.danger ? "text-theme-danger hover:bg-theme-danger-bright-soft" : ""
                }`}
              >
                {item.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
